import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useState } from 'react';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

interface CreateGmailLabelActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
}

export const CreateGmailLabelActionSchema = z.object({
  type: z.literal('Create_Gmail_Label'),
  gmailLabel: z.array(z.string()).min(1, 'A label is required.'),
});

export type CreateGmailLabelActionType = z.infer<
  typeof CreateGmailLabelActionSchema
>;

// Type guard to check if an action is CreateGmailLabelActionType
function isCreateGmailLabelAction(
  action: PolicyAction
): action is CreateGmailLabelActionType {
  return action.type === 'Create_Gmail_Label';
}

export default function CreateGmailLabelAction({
  setValue,
  watch,
  control,
  errors,
  trigger,
}: CreateGmailLabelActionProps) {
  const [labelError, setLabelError] = useState(false);
  const [labelName, setLabelName] = useState('');
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Create_Gmail_Label');
  const actionAtIndex = actionData[idx];
  if (!isCreateGmailLabelAction(actionAtIndex)) {
    return null;
  }

  const currentAction: CreateGmailLabelActionType = actionAtIndex;
  const error: FieldErrors<CreateGmailLabelActionType> | undefined =
    errors?.actionData?.[idx];

  function removeSelection(selectedLabel: string) {
    const newList = currentAction.gmailLabel.filter(
      (label) => label !== selectedLabel
    );
    setValue(`actionData.${idx}.gmailLabel`, [...newList]);
  }

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={1}>
        <Stack flexDirection={'row'} gap={1}>
          <Controller
            name={`actionData.${idx}.gmailLabel`}
            control={control}
            render={() => (
              <TextField
                inputProps={{
                  id: 'gmailLabel',
                }}
                InputLabelProps={{
                  htmlFor: 'gmailLabel',
                }}
                label="Label Name"
                value={labelName}
                onChange={(event) => setLabelName(event.target.value)}
                sx={{ width: 250 }}
                autoComplete="off"
                error={!!error?.gmailLabel || labelError}
                helperText={
                  labelError
                    ? 'Label is already selected.'
                    : error?.gmailLabel
                    ? error?.gmailLabel?.message
                    : ' '
                }
              />
            )}
          />
          <Box>
            <Button
              sx={{ height: 40 }}
              variant="contained"
              disabled={labelName.length === 0}
              onClick={() => {
                if (currentAction.gmailLabel.includes(labelName)) {
                  setLabelError(true);
                } else {
                  setValue(`actionData.${idx}.gmailLabel`, [
                    ...currentAction.gmailLabel,
                    labelName,
                  ]);
                  setLabelName('');
                  setLabelError(false);
                  trigger(`actionData.${idx}.gmailLabel`);
                }
              }}
            >
              Add
            </Button>
          </Box>
        </Stack>
        <Box sx={{ maxHeight: 200 }}>
          <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 200 }}>
            <TableContainer sx={{ minWidth: 470, maxHeight: 200 }}>
              <Table size={'small'} stickyHeader aria-label="selected-labels">
                <TableHead>
                  <TableRow>
                    <TableCell>Gmail Labels to Add</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentAction.gmailLabel
                    ? currentAction.gmailLabel.map((label) => (
                        <TableRow key={label}>
                          <TableCell>
                            <Stack
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography variant="body2">{label}</Typography>
                              <Tooltip arrow title="Delete">
                                <span>
                                  <IconButton
                                    onClick={() => {
                                      removeSelection(label);
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Stack>
    </Stack>
  );
}
